import React, { useState, useCallback } from 'react';
import { Map, AdvancedMarker, InfoWindow, Pin } from '@vis.gl/react-google-maps';
import { Box, Typography, CircularProgress, Paper, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ProviderLocation } from './types';
import { useClaimsMap } from './useClaimsMap';

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    padding: theme.spacing(3),
    backgroundColor: '#1a1f36',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    height: '600px',
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: 'white',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '500px',
  },
  errorText: {
    color: '#f44336',
    textAlign: 'center',
  },
  infoWindow: {
    color: '#000',
  },
  infoTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  infoDetail: {
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.25),
  },
}));

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '8px',
};

const center = {
  lat: 39.8283,
  lng: -98.5795,
};

interface ClaimsLocationMapProps {
  providerLocations?: ProviderLocation[];
  isLoading?: boolean;
}

export const ClaimsLocationMap: React.FC<ClaimsLocationMapProps> = ({
  providerLocations = [],
  isLoading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const { markers, geocodingError, getMarkerSize, getMarkerColorByProviderType } = useClaimsMap(providerLocations);

  const handleMarkerClick = useCallback((markerId: string) => {
    setSelectedMarkerId(markerId);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedMarkerId(null);
  }, []);

  if (isLoading) {
    return (
      <Paper className={classes.mapContainer}>
        <Typography className={classes.title}>{t('Claims Location Map')}</Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} color="primary" />
        </div>
      </Paper>
    );
  }

  if (geocodingError) {
    return (
      <Paper className={classes.mapContainer}>
        <Typography className={classes.title}>{t('Claims Location Map')}</Typography>
        <div className={classes.loadingContainer}>
          <Typography className={classes.errorText}>{geocodingError}</Typography>
        </div>
      </Paper>
    );
  }

  if (!providerLocations || providerLocations.length === 0) {
    return (
      <Paper className={classes.mapContainer}>
        <Typography className={classes.title}>{t('Claims Location Map')}</Typography>
        <div className={classes.loadingContainer}>
          <Typography>{t('No provider location data available')}</Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.mapContainer}>
      <Typography className={classes.title}>{t('Claims Location Map')}</Typography>
      <Box>
        <Map
          style={mapContainerStyle}
          defaultCenter={center}
          defaultZoom={4}
          gestureHandling={'greedy'}
          disableDefaultUI={false}
          mapId={process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID'}
        >
          {markers.map((marker, index) => {
            const markerId = `${marker.city}-${marker.state}-${index}`;
            const size = getMarkerSize(marker.claimCount);
            const color = getMarkerColorByProviderType(marker.label);

            return (
              <React.Fragment key={markerId}>
                <AdvancedMarker
                  position={marker.position}
                  onClick={() => handleMarkerClick(markerId)}
                >
                  <Pin
                    background={color}
                    borderColor={'#fff'}
                    glyphColor={'#fff'}
                    scale={size / 12}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#fff',
                      }}
                    >
                      {marker.label}
                    </div>
                  </Pin>
                </AdvancedMarker>

                {selectedMarkerId === markerId && (
                  <InfoWindow
                    position={marker.position}
                    onCloseClick={handleInfoWindowClose}
                  >
                    <div className={classes.infoWindow}>
                      <Typography className={classes.infoTitle}>
                        {marker.providerName}
                      </Typography>
                      <Typography className={classes.infoDetail}>
                        {marker.city}, {marker.state}
                      </Typography>
                      <Typography className={classes.infoDetail}>
                        {t('Claims')}: {marker.claimCount}
                      </Typography>
                    </div>
                  </InfoWindow>
                )}
              </React.Fragment>
            );
          })}
        </Map>
      </Box>
    </Paper>
  );
};
